import { useState } from "react";
import { Mail, MapPin, Heart, Phone, Globe } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Connect() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill out every field, thoughtfully.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/connect", form);
      toast.success("Thank you — we'll write back soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="connect-page" className="bg-brand-cream pt-16 pb-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2">
          <span className="text-xs uppercase tracking-[0.35em] text-brand-gold">
            Let&apos;s Connect
          </span>
          <h1 className="mt-3 font-serif text-5xl lg:text-6xl text-brand-moss leading-tight">
            Write to <span className="italic">us</span>.
          </h1>
          <p className="mt-5 text-lg text-brand-moss/75 leading-relaxed">
            Whether you&apos;d like a custom planter, a wholesale conversation, or
            simply to share a verse — we&apos;d love to hear from you.
          </p>

          <div className="mt-10 space-y-4 text-sm text-brand-moss/80">
            <p className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-brand-gold" /> 870-635-4407
            </p>
            <p className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-brand-gold" /> 414llc.info@gmail.com
            </p>
            <p className="flex items-center gap-3">
              <Globe className="w-4 h-4 text-brand-gold" /> www.414llc.com
            </p>
            <p className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-brand-gold" /> Made in the U.S.A.
            </p>
            <p className="flex items-center gap-3">
              <Heart className="w-4 h-4 text-brand-gold fill-current" /> Proud supporter of Autism Awareness
            </p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="lg:col-span-3 bg-brand-parchment border border-brand-moss/15 rounded-3xl p-8 lg:p-10 space-y-5"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.25em] text-brand-moss/60">Your Name</span>
              <input
                data-testid="contact-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 w-full bg-brand-cream border border-brand-moss/15 rounded-xl px-4 py-3 focus:outline-none focus:border-brand-gold transition-colors"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.25em] text-brand-moss/60">Your Email</span>
              <input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 w-full bg-brand-cream border border-brand-moss/15 rounded-xl px-4 py-3 focus:outline-none focus:border-brand-gold transition-colors"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.25em] text-brand-moss/60">Your Message</span>
            <textarea
              data-testid="contact-message"
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-2 w-full bg-brand-cream border border-brand-moss/15 rounded-xl px-4 py-3 focus:outline-none focus:border-brand-gold transition-colors resize-none"
            />
          </label>
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="inline-flex items-center justify-center bg-brand-moss text-brand-gold hover:bg-brand-moss-light px-8 py-3.5 rounded-full text-sm tracking-wide transition-colors disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </section>
  );
}
